import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AuthTokenAdapter } from '@shared/adapters/auth-token-adapter';
import { InterceptorRequest } from '@shared/adapters/http-interceptor-adapter';
import { AccessTokenError } from '@shared/models/token-model';
import { SharedHttpInterceptor } from '@shared/services/shared-http-interceptor';
import { catchError, EMPTY, from, mergeMap, Observable, throwError } from 'rxjs';
import { RENDERER_AUTH_TOKEN } from 'src/providers/renderer-auth-token-provider';

/**
 * RendererHttpInterceptor - Angular HttpClient Integration for Renderer Process
 *
 * This interceptor serves as the Angular-specific wrapper around SharedAuthInterceptor,
 * handling all HTTP authentication for requests made in the Angular renderer process.
 * Works in both Electron renderer and web browser environments.
 *
 * ARCHITECTURE:
 * - Registered with Angular's HTTP_INTERCEPTORS during app initialization
 * - Converts between Angular HttpRequest/HttpErrorResponse and SharedAuthInterceptor interfaces
 * - Delegates all authentication logic to SharedAuthInterceptor
 * - Never needs to detect environment (always runs in Angular context)
 *
 * REQUEST FLOW:
 * 1. Angular HTTP interceptor fires before each HTTP request
 * 2. Converts HttpRequest to InterceptorRequest format
 * 3. Calls SharedAuthInterceptor.intercept() for auth processing
 * 4. Converts response back to Angular HttpRequest with auth headers applied
 * 5. Request continues through Angular's HTTP pipeline
 *
 * ERROR HANDLING FLOW:
 * 1. Catches HttpErrorResponse (401 errors) using RxJS catchError
 * 2. Converts error to InterceptorError format
 * 3. Calls SharedAuthInterceptor.handleError() for token refresh
 * 4. If refresh succeeds, creates new Angular request and retries
 * 5. If refresh fails, triggers logout and propagates error
 *
 * DEPENDENCIES:
 * - SharedAuthInterceptor: Contains all authentication logic
 * - Angular HttpClient: HTTP client for making requests in renderer process
 * - RxJS: For reactive error handling and request transformation
 */

@Injectable()
export class RendererHttpInterceptor implements HttpInterceptor {
  /** Ensures we only redirect once */
  private static redirected = false;
  private sharedInterceptor: SharedHttpInterceptor;

  constructor(@Inject(RENDERER_AUTH_TOKEN) private authTokenAdapter: AuthTokenAdapter) {
    this.sharedInterceptor = new SharedHttpInterceptor(this.authTokenAdapter);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const interceptorRequest: InterceptorRequest = {
      url: request.url,
      method: request.method,
      headers: this.convertHeaders(request.headers),
      body: request.body,
    };

    return from(this.sharedInterceptor.intercept(interceptorRequest)).pipe(
      mergeMap((transformedRequest) => {
        const angularRequest = this.convertToAngularRequest(request, transformedRequest);
        return next.handle(angularRequest);
      }),
      catchError((error) => {
        if (error instanceof AccessTokenError && error.redirectUrl) {
          if (error.type === 'uninitialized' && window.location.pathname.includes('/auth')) {
            return EMPTY;
          }

          // Browser
          document.location.replace(error.redirectUrl);

          return EMPTY; // Abort the chain – nothing else will run
        }

        return throwError(() => error); // any other error
      })
    );
  }

  private convertHeaders(headers: HttpHeaders): Record<string, string> {
    const result: Record<string, string> = {};
    if (headers && headers.keys) {
      headers.keys().forEach((key: string) => {
        const value = headers.get(key);
        if (value) {
          result[key] = value;
        }
      });
    }
    return result;
  }

  private convertToAngularRequest(
    originalRequest: HttpRequest<unknown>,
    interceptorRequest: InterceptorRequest
  ): HttpRequest<unknown> {
    return originalRequest.clone({
      url: interceptorRequest.url,
      setHeaders: interceptorRequest.headers,
      body: interceptorRequest.body,
    });
  }
}
